import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Download, Trash2, Users, CheckCircle2, Clock, HeartOff } from "lucide-react";

function KPI({ icon: Icon, label, value, tone, tid }) {
  const bg = { active: "bg-emerald-500/15 text-emerald-600", pending: "bg-amber-500/15 text-amber-600", unsub: "bg-rose-500/15 text-rose-600" }[tone] || "bg-primary/10 text-primary";
  return (
    <Card className="rounded-2xl border-2" data-testid={tid}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${bg}`}><Icon className="w-4 h-4" /></div>
        <div><div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div><div className="font-heading font-bold text-2xl mt-0.5">{value}</div></div>
      </CardContent>
    </Card>
  );
}

export default function AdminCitizens() {
  const [items, setItems] = useState([]);
  const [interests, setInterests] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [interest, setInterest] = useState("all");

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    if (interest !== "all") params.interest = interest;
    const [c, s] = await Promise.all([
      api.get("/admin/citizens", { params }),
      api.get("/admin/citizens/stats"),
    ]);
    setItems(c.data); setStats(s.data);
  };
  useEffect(() => { api.get("/interests").then(({ data }) => setInterests(data)); }, []);
  useEffect(() => { const t = setTimeout(load, 220); return () => clearTimeout(t); }, [q, status, interest]);

  const remove = async (c) => {
    if (!window.confirm(`RGPD · Eliminar totes les dades de ${c.email}? Aquesta acció és irreversible.`)) return;
    await api.delete(`/admin/citizens/${c.id}`);
    toast.success("Eliminat i historial anònim");
    load();
  };

  const exportOne = async (c) => {
    const { data } = await api.get(`/admin/citizens/${c.id}/export`);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `citizen_${c.email}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportAll = () => {
    const rows = [["email", "name", "phone", "language", "interests", "status", "consent_given_at"]];
    items.forEach(c => rows.push([c.email, c.name, c.phone || "", c.language, (c.interests || []).join("|"), c.status, c.consent_given_at || ""]));
    const csv = rows.map(r => r.map(x => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "ciutadans.csv"; a.click();
  };

  const statusBadge = (s) => ({
    active: <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-300">Actiu</Badge>,
    pending: <Badge className="bg-amber-500/15 text-amber-700 border-amber-300">Pendent confirmació</Badge>,
    unsubscribed: <Badge className="bg-rose-500/15 text-rose-700 border-rose-300">Baixa</Badge>,
  }[s] || <Badge variant="secondary">{s}</Badge>);

  return (
    <div className="space-y-6" data-testid="admin-citizens">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">CRM</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Ciutadans registrats</h1>
          <p className="text-sm text-muted-foreground mt-2">Base de dades diferenciada d'associats. Registres voluntaris amb consentiment RGPD.</p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={exportAll} data-testid="citizens-export-csv">
          <Download className="w-4 h-4 mr-2" /> Exportar CSV
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI icon={Users} label="Total" value={stats.total} tid="citizens-kpi-total" />
          <KPI icon={CheckCircle2} label="Actius" value={stats.active} tone="active" tid="citizens-kpi-active" />
          <KPI icon={Clock} label="Pendents" value={stats.pending} tone="pending" tid="citizens-kpi-pending" />
          <KPI icon={HeartOff} label="Baixes" value={stats.unsubscribed} tone="unsub" tid="citizens-kpi-unsub" />
        </div>
      )}

      <Card className="rounded-2xl border-2"><CardContent className="p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-56">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cerca per nom, email o telèfon…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full" data-testid="citizens-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48 rounded-full" data-testid="citizens-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tots els estats</SelectItem>
            <SelectItem value="active">Actius</SelectItem>
            <SelectItem value="pending">Pendents</SelectItem>
            <SelectItem value="unsubscribed">Baixes</SelectItem>
          </SelectContent>
        </Select>
        <Select value={interest} onValueChange={setInterest}>
          <SelectTrigger className="w-56 rounded-full" data-testid="citizens-interest-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Qualsevol interès</SelectItem>
            {interests.map(i => <SelectItem key={i.slug} value={i.slug}>{i.name_ca}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardContent></Card>

      <Card className="rounded-2xl border-2"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Idioma</TableHead><TableHead>Interessos</TableHead><TableHead>Estat</TableHead><TableHead>Data</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(c => (
              <TableRow key={c.id} data-testid={`citizen-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-xs">{c.email}</TableCell>
                <TableCell className="uppercase text-xs">{c.language}</TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{(c.interests || []).map(s => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}</div></TableCell>
                <TableCell>{statusBadge(c.status)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right flex gap-1 justify-end">
                  <Button size="icon" variant="ghost" title="Exportar dades (RGPD)" onClick={() => exportOne(c)} data-testid={`citizen-export-${c.id}`}><Download className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="text-destructive" title="Eliminar (RGPD)" onClick={() => remove(c)} data-testid={`citizen-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Cap ciutadà registrat encara.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
